import React, { useMemo } from 'react';

interface ZincGranulesProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  /** Approximate radius of the pile footprint. */
  pileRadius?: number;
  /** Number of granule chunks to render. */
  count?: number;
}

/**
 * Zinc granules — a small pile of metallic grey-silver granules used as
 * the metal reactant for the H₂ gas-generation step (Zn + 2 HCl →
 * ZnCl₂ + H₂↑). Deterministic placement so the pile doesn't jitter
 * between renders.
 */
export function ZincGranules({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  pileRadius = 0.045,
  count = 18,
}: ZincGranulesProps) {
  const seeds = useMemo(() => {
    const out: { x: number; y: number; z: number; r: number; rotX: number; rotY: number; rotZ: number; tint: number }[] = [];
    let s = 91;
    const rand = () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
    for (let i = 0; i < count; i++) {
      const theta = rand() * Math.PI * 2;
      const r = Math.sqrt(rand()) * pileRadius;
      out.push({
        x: Math.cos(theta) * r,
        y: rand() * 0.008,
        z: Math.sin(theta) * r,
        r: 0.005 + rand() * 0.005,
        rotX: rand() * Math.PI,
        rotY: rand() * Math.PI,
        rotZ: rand() * Math.PI,
        tint: rand(),
      });
    }
    return out;
  }, [count, pileRadius]);

  return (
    <group position={position} rotation={rotation} scale={scale}>
      {seeds.map((g, i) => (
        <mesh
          key={i}
          position={[g.x, g.y, g.z]}
          rotation={[g.rotX, g.rotY, g.rotZ]}
          castShadow
        >
          <dodecahedronGeometry args={[g.r, 0]} />
          <meshStandardMaterial
            color={g.tint > 0.5 ? '#aab0b8' : '#9398a0'}
            metalness={0.65}
            roughness={0.45}
          />
        </mesh>
      ))}
    </group>
  );
}

export default ZincGranules;
